import { createSelector } from '@reduxjs/toolkit';
import { differenceInCalendarDays } from 'date-fns';

import { Store } from 'components/common/types/Store.types';
import { FileList } from 'components/common/types';
import { hasDaypartTargeting } from 'components/common/DaypartTargeting/utils';
import { Line } from 'components/common/types/Deal.types';

export type MultiTargetingConflictType = 'PATTERN' | 'LIST' | 'MANUAL_SOT';

export const MULTI_TARGETING_CONFLICT_MESSAGES: Record<MultiTargetingConflictType, string> = {
  PATTERN: 'Pattern',
  LIST: 'List',
  MANUAL_SOT: 'Manual Share of Time',
};

const hasListTargeting = ({ listFiles, routeFrameCodes, mapRouteFrameCodes }: Line): boolean =>
  !!(listFiles?.length || routeFrameCodes?.length || mapRouteFrameCodes?.length);

const hasPatternTargeting = ({ selectedDays, patternRepeatType, startDate, endDate }: Line): boolean => {
  const dateRangeLength = differenceInCalendarDays(new Date(endDate ?? ''), new Date(startDate ?? ''));

  return hasDaypartTargeting(selectedDays, patternRepeatType, dateRangeLength + 1);
};

const hasManualSotTargeting = (sot: number | undefined): boolean => sot != null;

export const checkMultiTargetingConflicts = (line: Partial<Line>): MultiTargetingConflictType[] => {
  const conflicts: MultiTargetingConflictType[] = [];

  if (hasPatternTargeting(line as Line)) {
    conflicts.push('PATTERN');
  }

  if (hasListTargeting(line as Line)) {
    conflicts.push('LIST');
  }

  if (hasManualSotTargeting(line.sot)) {
    conflicts.push('MANUAL_SOT');
  }

  return conflicts;
};

export const createMultiTargetingConflictMessage = (conflicts: MultiTargetingConflictType[]): string | null => {
  if (!conflicts.length) return null;

  const conflictsMessages = conflicts.map((conflict) => MULTI_TARGETING_CONFLICT_MESSAGES[conflict]).join(', ');
  return `Identified conflicts with existing ${conflictsMessages} targeting.`;
};

export const getIsMultiTargetingFile = createSelector(
  (state: Store) => state.dealManagement.commonDeal.currentLine.multiTargetingFile,
  (multiTargetingFile) => Boolean(multiTargetingFile),
);

export const getMultiTargetingFileByLineId = (state: Store, lineId: string): FileList | null => {
  const { currentLine } = state.dealManagement.commonDeal;

  if (currentLine.lineId === lineId || currentLine.id === lineId) {
    return currentLine.multiTargetingFile;
  }

  const line = state.dealManagement.backupFormData.lines.find(
    (backupLine) => backupLine.lineId === lineId || backupLine.id === lineId,
  );

  return line?.multiTargetingFile ?? null;
};

export const getMultiTargetingConflicts = createSelector(
  (state: Store) => state.dealManagement.commonDeal.currentLine,
  (multiTargetingConflicts) => checkMultiTargetingConflicts(multiTargetingConflicts),
);

export const getMultiTargetingConflictMessage = createSelector(getMultiTargetingConflicts, (multiTargetingConflicts) =>
  createMultiTargetingConflictMessage(multiTargetingConflicts),
);
